// src/pages/RegisterPage.js
import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaUser, FaBuilding, FaInfoCircle } from 'react-icons/fa';

const RegisterPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialType = searchParams.get('type') === 'company' ? 'company' : 'personal';
    const [accountType, setAccountType] = useState(initialType); // 'personal' or 'company'

    // Form fields
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [displayName, setDisplayName] = useState(''); // Personal name or company contact
    const [companyName, setCompanyName] = useState(''); // Company official name
    const [companyPhone, setCompanyPhone] = useState(''); // Company phone
    const [kvkkAccepted, setKvkkAccepted] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const { register } = useAuth();
    const navigate = useNavigate();

    const handleTabChange = (type) => {
        setAccountType(type);
        setSearchParams(type === 'company' ? { type: 'company' } : {});
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password !== confirmPassword) return setError('Şifreler eşleşmiyor!');
        if (password.length < 6) return setError('Şifre en az 6 karakter olmalıdır.');
        if (!kvkkAccepted) return setError('Lütfen KVKK metnini onaylayınız.');

        const isCompany = accountType === 'company';
        const finalDisplayName = isCompany ? companyName.trim() : displayName.trim();

        if (!finalDisplayName) {
            return setError(isCompany ? 'Şirket ticari unvanı boş bırakılamaz.' : 'Ad Soyad alanı boş bırakılamaz.');
        }

        try {
            setLoading(true);
            await register(email, password, finalDisplayName, isCompany);

            if (isCompany) {
                alert("Rent a Car şirket başvurunuz başarıyla alınmıştır. Yönetici onayının ardından filo panelinize erişebilirsiniz.");
                navigate('/login');
            } else {
                navigate('/verify-contact');
            }
        } catch (err) {
            console.error("Registration error:", err);
            if (err.code === 'auth/email-already-in-use') {
                setError('Bu e-posta adresi zaten kullanımda.');
            } else if (err.code === 'auth/invalid-email') {
                setError('Geçersiz bir e-posta adresi girdiniz.');
            } else {
                setError(`Kayıt başarısız oldu: ${err.message || 'Lütfen tekrar deneyin.'}`);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full min-h-[100dvh] bg-gray-50 flex flex-col items-center justify-center py-10 px-4">
            <div className="w-full max-w-md bg-white shadow-xl rounded-2xl px-6 sm:px-8 py-8 border border-gray-100">
                <h2 className="text-2xl font-extrabold mb-2 text-center text-gray-900">Hesap Oluştur</h2>
                <p className="text-xs text-gray-500 mb-6 text-center">İhtiyacınıza uygun hesap türünü seçerek devam edin</p>

                {/* Account Type Segmented Tab Switcher */}
                <div className="flex bg-gray-100 p-1 rounded-xl mb-6">
                    <button
                        type="button"
                        onClick={() => handleTabChange('personal')}
                        className={`flex-1 flex items-center justify-center py-2.5 text-xs sm:text-sm font-semibold rounded-lg transition-all ${
                            accountType === 'personal'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-500 hover:text-gray-800'
                        }`}
                    >
                        <FaUser className="mr-2 text-blue-600" /> Bireysel Araç Sahibi
                    </button>
                    <button
                        type="button"
                        onClick={() => handleTabChange('company')}
                        className={`flex-1 flex items-center justify-center py-2.5 text-xs sm:text-sm font-semibold rounded-lg transition-all ${
                            accountType === 'company'
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-500 hover:text-gray-800'
                        }`}
                    >
                        <FaBuilding className="mr-2 text-orange-600" /> Rent A Car / Filo
                    </button>
                </div>

                {error && <p className="bg-red-50 text-red-700 border border-red-200 p-3 rounded-lg mb-4 text-xs font-medium">{error}</p>}

                {accountType === 'company' && (
                    <div className="bg-orange-50 border border-orange-200 rounded-xl p-3 mb-5 flex items-start text-xs text-orange-900">
                        <FaInfoCircle className="text-orange-500 mt-0.5 mr-2 flex-shrink-0 text-base" />
                        <div>
                            <strong>Rent a Car İşletmesi Başvurusu:</strong> Kayıt sonrası filo paneli ve araç QR kodlarınız yönetici onayının ardından aktif olacaktır.
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    {accountType === 'personal' ? (
                        <div>
                            <label className="block text-gray-700 text-xs font-semibold mb-1" htmlFor="displayName">
                                Adınız ve Soyadınız
                            </label>
                            <input
                                type="text"
                                id="displayName"
                                required
                                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Örn: Ahmet Yılmaz"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                            />
                        </div>
                    ) : (
                        <>
                            <div>
                                <label className="block text-gray-700 text-xs font-semibold mb-1" htmlFor="companyName">
                                    Şirket / Acente Ticari Unvanı
                                </label>
                                <input
                                    type="text"
                                    id="companyName"
                                    required
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    placeholder="Örn: Ege Rent A Car Ltd. Şti."
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                />
                            </div>
                            <div>
                                <label className="block text-gray-700 text-xs font-semibold mb-1" htmlFor="companyPhone">
                                    İşletme İletişim Telefonu
                                </label>
                                <input
                                    type="tel"
                                    id="companyPhone"
                                    required
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
                                    placeholder="05XXXXXXXXX veya Sabit Hat"
                                    value={companyPhone}
                                    onChange={(e) => setCompanyPhone(e.target.value)}
                                />
                            </div>
                        </>
                    )}

                    <div>
                        <label className="block text-gray-700 text-xs font-semibold mb-1" htmlFor="email">
                            {accountType === 'company' ? 'Kurumsal E-posta Adresi' : 'E-posta Adresi'}
                        </label>
                        <input
                            type="email"
                            id="email"
                            required
                            className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="ornek@alanadi.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-gray-700 text-xs font-semibold mb-1" htmlFor="password">
                                Şifre
                            </label>
                            <input
                                type="password"
                                id="password"
                                required
                                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="En az 6 karakter"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 text-xs font-semibold mb-1" htmlFor="confirmPassword">
                                Şifre Tekrar
                            </label>
                            <input
                                type="password"
                                id="confirmPassword"
                                required
                                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Şifrenizi tekrar girin"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="pt-2">
                        <label className="flex items-start text-xs text-gray-600 cursor-pointer">
                            <input
                                type="checkbox"
                                className="form-checkbox h-4 w-4 text-blue-600 rounded mt-0.5"
                                required
                                checked={kvkkAccepted}
                                onChange={(e) => setKvkkAccepted(e.target.checked)}
                            />
                            <span className="ml-2">
                                <Link to="/privacy-kvkk" target="_blank" className="text-blue-600 underline font-medium">KVKK Metnini</Link> ve kullanıcı sözleşmesini okudum, onaylıyorum.
                            </span>
                        </label>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className={`w-full py-3 rounded-xl font-bold text-sm text-white transition-all shadow-md disabled:opacity-50 ${
                            accountType === 'company'
                                ? 'bg-orange-600 hover:bg-orange-700'
                                : 'bg-gray-900 hover:bg-black'
                        }`}
                    >
                        {loading
                            ? 'Kaydediliyor...'
                            : accountType === 'company'
                                ? 'Rent a Car Başvurusunu Tamamla'
                                : 'Bireysel Hesap Oluştur'
                        }
                    </button>
                </form>

                <div className="text-center text-xs text-gray-500 mt-6 pt-4 border-t border-gray-100">
                    Zaten bir hesabınız var mı?{' '}
                    <Link to="/login" className="text-blue-600 hover:underline font-bold">Giriş Yap</Link>
                </div>
            </div>
        </div>
    );
};

export default RegisterPage;